import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment } from 'react';
import { ClausePathRoot, SubClause, SubClauseProvider, sectionAnchorId, useCustomSectionsAfter, useCustomSectionsOrdered, useSectionNumber, } from './section-numbering.js';
import { customSectionAnchorRef, } from '../custom-sections.js';
/**
 * Numbered top-level section. The § number renders in the gutter so the
 * body text aligns under headings. Tries hard not to break across a page
 * — `break-inside: avoid` is a strong hint, not an absolute guarantee for
 * long sections. Wrap intentionally-atomic sub-blocks in <KeepTogether>.
 *
 * Numbering (per ADR-0049): pass `sectionRef` and the number is resolved
 * from the nearest `<SectionNumberingProvider>` — computed from visibility
 * so conditional sections never leave a gap, and the section gets a stable
 * DOM anchor (`id="section-<ref>"`) the editor scrolls to on field focus.
 * When the section is gated out for the current values, this renders null
 * (no per-section `cond()` guard needed in the Body). Legacy templates may
 * still pass a literal `n` instead; that path keeps working unchanged.
 *
 * Per-contract custom sections (ADR-0060) are emitted here too: any custom
 * section anchored after this section's `ref` renders as a *sibling* right
 * after it (a Fragment, not a child — so each keeps its own page-break and
 * §-level styling). This is the single "layout-sourced" hook for placement;
 * templates need no per-position slots.
 */
export function Section(props) {
    const { number, hidden } = useSectionNumber(props.sectionRef ?? '');
    const anchored = useCustomSectionsAfter(props.sectionRef ?? '');
    if (props.sectionRef && hidden)
        return null;
    const displayNumber = props.sectionRef ? number : props.n;
    return (_jsxs(_Fragment, { children: [_jsxs("section", { id: props.sectionRef ? sectionAnchorId(props.sectionRef) : undefined, className: "mt-4 mb-2", style: { breakInside: 'avoid' }, children: [_jsxs("h2", { className: "mt-1.5 mb-1.5 flex gap-2 border-b border-stone-300 pb-1 text-[12px] font-bold", children: [_jsxs("span", { className: "min-w-[1.6em] text-stone-600", children: ["\u00A7 ", displayNumber] }), _jsx("span", { children: props.title })] }), _jsx("div", { className: "text-[11px] leading-relaxed", children: _jsx(ClausePathRoot, { path: displayNumber != null ? String(displayNumber) : '', children: props.children }) })] }), anchored.map((cs) => (_jsx(CustomSectionRender, { section: cs }, cs.id)))] }));
}
/** Render one component (key→value rows or a free-text paragraph). */
function renderCustomComponent(c) {
    if (c.kind === 'text') {
        return (_jsx("p", { className: "m-0 whitespace-pre-wrap", children: c.body }, c.id));
    }
    return (_jsx(Fragment, { children: c.rows
            .filter((r) => r.key.trim() !== '' || r.value.trim() !== '')
            .map((r, i) => (_jsx(FieldRow, { label: r.key, children: r.value }, i))) }, c.id));
}
/**
 * A per-contract custom section (ADR-0060). Renders as a normal numbered
 * `<Section>` (so it shares the numbering / anchor machinery), then walks its
 * sub-sections: a *named* sub-section is a numbered `<SubClause>` (§N.M); a
 * *blend* sub-section renders its components inline as plain section content.
 * Components render in declaration order.
 */
function CustomSectionRender({ section, }) {
    const named = section.subSections.filter((ss) => (ss.title ?? '').trim() !== '');
    return (_jsx(Section, { sectionRef: customSectionAnchorRef(section.id), title: section.title, children: _jsx(SubClauseProvider, { entries: named.map((ss) => ({ id: ss.id, visible: true })), children: section.subSections.map((ss) => {
                const components = ss.components.map(renderCustomComponent);
                return (ss.title ?? '').trim() !== '' ? (_jsx(SubClause, { id: ss.id, title: ss.title, children: components }, ss.id)) : (_jsx(Fragment, { children: components }, ss.id));
            }) }) }));
}
/**
 * Frame-less custom-section body (per ADR-0061): renders ALL of the
 * contract's custom sections, in array order, as top-level numbered
 * sections. Used by templates whose entire body is user-authored content
 * (the clean bilaga) — they wrap this in `<SectionNumberingProvider sections={[]} …>`,
 * which numbers the customs `1..N`. Renders nothing when there are none.
 */
export function CustomSectionsBody() {
    const sections = useCustomSectionsOrdered();
    if (sections.length === 0)
        return null;
    return (_jsx(_Fragment, { children: sections.map((cs) => (_jsx(CustomSectionRender, { section: cs }, cs.id))) }));
}
/**
 * Two-column field row (label | value) used inside sections to render
 * variable fields. Default columns are 40 / 60 but the SPA can override
 * via a className-merge approach later if needed.
 */
export function FieldRow(props) {
    return (_jsxs("div", { className: "grid gap-2 border-b border-dotted border-stone-200 py-1", style: { gridTemplateColumns: '40% 60%' }, children: [_jsx("span", { className: "min-w-0 font-sans text-[10px] text-stone-600", style: { overflowWrap: 'anywhere' }, children: props.label }), _jsx("span", { className: "min-w-0 text-[11px] leading-relaxed", style: { overflowWrap: 'anywhere' }, children: props.children })] }));
}
//# sourceMappingURL=Section.js.map